/**
 * BoardCardNode — the one custom react-flow node component, keyed by
 * `data.cardType` (looked up in NODE_TYPES) rather than one component per
 * type, to avoid duplicating the same card chrome 8 times.
 */

import React from 'react';
import { Handle, Position } from '@xyflow/react';
import { BookOpen } from 'lucide-react';
import { cx } from '@/components/site/primitives';
import { NODE_TYPE_MAP, STATUS_OPTIONS } from './constants';

const TONE_SOLID = {
  coral: 'bg-coral/12 text-coral',
  sage: 'bg-sage/45 text-[#4f6249] dark:bg-sage/20 dark:text-[#b3c7aa]',
  yellow: 'bg-yellow/30 text-[#8a6a29] dark:bg-yellow/15 dark:text-[#e2bd78]',
  blue: 'bg-blue/20 text-[#4d616c] dark:bg-blue/20 dark:text-[#aabecb]',
  cream: 'bg-[#F1E9DC] text-ink dark:bg-white/5',
};

const TONE_BORDER = {
  coral: 'border-coral/35',
  sage: 'border-sage/60',
  yellow: 'border-yellow/50',
  blue: 'border-blue/45',
  cream: 'border-line',
};

const HANDLES = [
  { id: 'top', position: Position.Top },
  { id: 'right', position: Position.Right },
  { id: 'bottom', position: Position.Bottom },
  { id: 'left', position: Position.Left },
];

function CardHandles() {
  return HANDLES.map(({ id, position }) => (
    <Handle
      key={id}
      id={id}
      type="source"
      position={position}
      className="!h-2.5 !w-2.5 !border-2 !border-card !bg-coral/70 opacity-0 transition-opacity group-hover:opacity-100"
    />
  ));
}

export default function BoardCardNode({ data, selected }) {
  const config = NODE_TYPE_MAP[data.cardType] ?? NODE_TYPE_MAP.idea;
  const Icon = config.icon;
  const statusLabel = STATUS_OPTIONS.find((s) => s.value === data.status)?.label;
  const characterCount = data.characterIds?.length ?? 0;

  return (
    <div
      onClick={() => data.onOpen?.()}
      className={cx(
        'group relative flex w-[220px] cursor-pointer flex-col gap-1.5 rounded-xl border bg-card p-3 shadow-card transition-all duration-200',
        TONE_BORDER[config.tone],
        selected && 'ring-2 ring-coral ring-offset-2 ring-offset-cream',
        data.muted && 'opacity-30 grayscale'
      )}
    >
      <CardHandles />

      <div className="flex items-center gap-2">
        <span className={cx('grid h-6 w-6 shrink-0 place-items-center rounded-full', TONE_SOLID[config.tone])}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <p className="min-w-0 flex-1 truncate font-display text-sm font-semibold leading-tight text-ink">
          {data.title}
        </p>
      </div>

      {data.description && (
        <p className="line-clamp-2 text-xs leading-snug text-subtle">{data.description}</p>
      )}

      <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
        {statusLabel && (
          <span className="rounded-full bg-cream px-2 py-0.5 text-[10px] font-semibold text-subtle">
            {statusLabel}
          </span>
        )}
        {data.chapterId && (
          <span className="flex items-center gap-1 rounded-full bg-cream px-2 py-0.5 text-[10px] font-semibold text-subtle">
            <BookOpen className="h-2.5 w-2.5" /> Linked
          </span>
        )}
        {characterCount > 0 && (
          <span className="rounded-full bg-cream px-2 py-0.5 text-[10px] font-semibold text-subtle">
            {characterCount} character{characterCount > 1 ? 's' : ''}
          </span>
        )}
      </div>
    </div>
  );
}
